import React, { useEffect, useRef, useState } from 'react';
import { BlisterPackBadge } from './blisterPackBadge'

import Axios from '../../networking/interceptor';
import { driver_patient_search } from '../../networking/urlEndPoints';
import { useAuthSelector } from '../../services/selectors/authSelector';
import { toTitleCase } from '../../utils/helper/routeAuditUi';

import ThemeTextInput from '../form/textInput';

import * as Icons from '../../assets/svg/icons';
import * as Hooks from '../../hooks';
import * as routeNames from '../../routes/routeName';

const SearchBox = ({
	wrapperClassName = "",
	inputClassName = "",
	dropdownClassName = "",
	onRefresh,
	isRefreshing = false,
}) => {
	const MIN_QUERY_LENGTH = 2;
	const SEARCH_DEBOUNCE_MS = 450;

	const { pharmacyId } = useAuthSelector();
	const navigate = Hooks.useCustomNavigate();
	const location = Hooks.useCustomLocation();

	const [query, setQuery] = useState("");
	const [show, setShow] = useState(false);
	const [results, setResults] = useState([]);
	const [isLoading, setIsLoading] = useState(false);

	const activeQueryRef = useRef(null);

	useEffect(() => {
		activeQueryRef.current = null;
		setResults([]);
	}, [pharmacyId]);

	useEffect(() => {
		const trimmed = query.trim();

		if (trimmed.length < MIN_QUERY_LENGTH) {
			setResults([]);
			setIsLoading(false);
			return;
		}

		const requestKey = `${pharmacyId}:${trimmed}`;
		activeQueryRef.current = requestKey;
		setIsLoading(true);

		const debounceTimer = setTimeout(() => {
			Axios.get(driver_patient_search, {
				params: { pharmacy_id: pharmacyId, query: trimmed },
			})
				.then((response) => {
					const data = response.data?.data || [];
					if (activeQueryRef.current === requestKey) {
						setResults(data);
						setIsLoading(false);
					}
				})
				.catch(() => {
					if (activeQueryRef.current === requestKey) {
						setResults([]);
						setIsLoading(false);
					}
				});
		}, SEARCH_DEBOUNCE_MS);

		return () => clearTimeout(debounceTimer);
	}, [query, pharmacyId]);

	const handlePatientSelect = (id) => {
		const searchParams = new URLSearchParams(location.search);
		searchParams.set('id', id);
		navigate({
			pathname: routeNames?.search_patient_data,
			search: `?${searchParams.toString()}`,
		});
	};

	const isHomePage = [
		routeNames.HOME_ROUTE,
		routeNames.DASHBOARD_PHARMACY,
		'/dashboard-pharmacy',
	].includes(location.pathname);
	const showDropdown = show && query.trim().length >= MIN_QUERY_LENGTH;

	const LoadingSpinner = () => (
		<svg className="w-4 h-4 text-blue-600 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
			<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
			<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
		</svg>
	);

	return (
		<form className={`w-full flex-1 relative ${wrapperClassName}`.trim()}>
			<div className="flex items-center gap-3">
				{!isHomePage ? (
					<button
						type="button"
						onClick={() => navigate(routeNames.DASHBOARD_PHARMACY)}
						className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-slate-50 text-slate-700 transition hover:bg-slate-100"
						aria-label="Go to homepage"
					>
						<Icons.HomeIcon />
					</button>
				) : null}
				<div className="relative flex-1 h-12">
					<ThemeTextInput
						icon={() => <Icons.SearchIcon />}
						value={query}
						placeholder="Search Patients"
						onChange={(e) => setQuery(e.target.value)}
						inputClassName={`h-12 text-[16px] ${inputClassName}`.trim()}
						inputProps={{
							sizing: 'lg',
							style: { fontSize: 16 },
							onFocus: () => setShow(true),
							onBlur: () => setTimeout(() => setShow(false), 150),
							theme: {
								field: {
									input: {
										colors: {
											gray: 'border-gray-300 bg-gray-50  text-gray-900 focus:border-gray-300 focus:ring-0 ',
										},
									},
								},
							},
						}}
						isBottomMargin={false}
					/>
					{isLoading && (
						<div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center" aria-hidden="true">
							<LoadingSpinner />
						</div>
					)}
				</div>
				{typeof onRefresh === 'function' ? (
					<button
						type="button"
						onClick={onRefresh}
						disabled={isRefreshing}
						className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-slate-50 text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
						aria-label="Refresh page data"
						title="Refresh"
					>
						<Icons.RefreshCwIcon className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
					</button>
				) : null}
			</div>

			{showDropdown && (
				<div
					id="dropdown"
					className={`absolute mt-1 overflow-auto z-10 w-full transition-all duration-200 ease-out
						${results.length >= 0
							? 'bg-white max-h-52 rounded-lg shadow-lg border border-gray-100'
							: ''} divide-y divide-gray-100 ${dropdownClassName}`.trim()}
				>
					<ul className="py-2 text-[16px] text-gray-700" aria-labelledby="dropdownDefaultButton">
						{query.trim().length < MIN_QUERY_LENGTH ? (
							<li className="px-4 py-3 text-[16px] text-gray-500">
								Type at least {MIN_QUERY_LENGTH} characters.
							</li>
						) : isLoading ? (
							<li className="px-4 py-3 text-[16px] text-gray-500">
								Searching…
							</li>
						) : results.length === 0 ? (
							<li className="px-4 py-3 text-[16px] text-gray-500">
								No Patient Found.
							</li>
						) : (
							results.map((patient, index) => (
								<li key={index}>
									<button
										type="button"
										className="text-left text-[16px] px-4 w-full py-2.5 hover:bg-gray-50 flex items-center justify-between transition-colors duration-150"
										onClick={() => handlePatientSelect(patient.id)}
									>
										<div>
											<p className="flex items-center gap-2">
												<span>{patient?.last_name} {patient?.first_name} {patient?.route && <>- {patient?.route}</>}</span>
												{patient?.is_blister && (
													<BlisterPackBadge />
												)}
											</p>
											{patient?.delivery_status !== 'delivered' && patient?.route_access?.allowed === false ? (
												<p className="text-xs text-red-600">Out of route. Delivery blocked.</p>
											) : null}
										</div>
										<div className="flex flex-col items-end">
											<span className="font-bold text-sm uppercase rounded-full" style={{ color: 'black' }}>
												{patient?.delivery_status === 'pending' || patient?.delivery_status === 'final_missed' || patient?.delivery_status === 'today_missed' ? `[P]` :
													patient?.delivery_status === 'delivered' ? `[D]` :
														patient?.delivery_status === 'missed' ? `[M]` : null}
											</span>
											{patient?.delivery_status !== 'delivered' && patient?.assigned_route_owner?.name ? (
												<span className="text-[11px] text-gray-500">{toTitleCase(patient.assigned_route_owner.name)}</span>
											) : null}
										</div>
									</button>
								</li>
							))
						)}
					</ul>
				</div>
			)}
		</form>
	);
};

export default SearchBox;
